(() => {
  'use strict';

  const CLARITY_EVENTS = {
    size_selected: { name: 'evano_size_selected', stage: 'size_selected' },
    add_to_cart_success: { name: 'evano_cart_success', stage: 'cart' },
    add_to_cart_error: { name: 'evano_cart_error', stage: 'cart_error' },
    checkout_preflight_completed: {
      name: 'evano_checkout_intent',
      stage: 'checkout_intent'
    },
    checkout_inventory_blocked: {
      name: 'evano_checkout_blocked',
      stage: 'checkout_blocked'
    }
  };
  const DEDUPE_WINDOW_MS = 2000;
  const MAX_SIGNATURES = 80;
  const recentEvents = new Map();

  const eventSignature = (payload, mappedEvent) => {
    const attemptId = payload.checkout_attempt_id || '';
    if (attemptId) return `${mappedEvent.name}:${attemptId}`;
    if (mappedEvent.name === 'evano_cart_success') {
      return [
        mappedEvent.name,
        payload.variant_id || '',
        payload.quantity || ''
      ].join(':');
    }

    return [
      mappedEvent.name,
      payload.source || '',
      payload.variant_id || '',
      payload.size || '',
      payload.quantity || '',
      payload.status || ''
    ].join(':');
  };

  const rememberEvent = (signature) => {
    const now = Date.now();
    const previous = recentEvents.get(signature);
    if (previous && now - previous < DEDUPE_WINDOW_MS) return false;

    recentEvents.set(signature, now);
    if (recentEvents.size > MAX_SIGNATURES) {
      const oldest = recentEvents.keys().next().value;
      recentEvents.delete(oldest);
    }
    return true;
  };

  const forwardToClarity = (payload) => {
    const mappedEvent = CLARITY_EVENTS[payload?.event];
    if (!mappedEvent || typeof window.clarity !== 'function') return;

    const signature = eventSignature(payload, mappedEvent);
    if (!rememberEvent(signature)) return;

    window.clarity('event', mappedEvent.name);
    window.clarity('set', 'evano_funnel_stage', mappedEvent.stage);
    if (payload.source) {
      window.clarity('set', 'evano_action_source', String(payload.source));
    }
  };

  const publishCroEvent = (event, detail = {}) => {
    const payload = { event, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    forwardToClarity(payload);
  };

  document.addEventListener('evano:cro-event', (event) => {
    forwardToClarity(event.detail || {});
  });

  // Gecko emits this after its native AJAX add-to-cart flow. Editorial PDP
  // actions publish their own success event and do not depend on this fallback.
  document.addEventListener('ajaxProduct:added', (event) => {
    const detail = event.detail || {};
    const variant = detail.variant || detail.product || {};
    publishCroEvent('add_to_cart_success', {
      source: 'theme_ajax',
      variant_id: String(
        detail.variant_id || detail.variantId || variant.id || detail.id || ''
      ),
      quantity: Number(detail.quantity) || 1
    });
  });

  const requestCartDetails = (body) => {
    const read = (key) => {
      const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
      const isSearchParams = typeof URLSearchParams !== 'undefined'
        && body instanceof URLSearchParams;
      if (isFormData || isSearchParams) {
        return body.get(key);
      }
      if (typeof body === 'string') return new URLSearchParams(body).get(key);
      return body && typeof body === 'object' ? body[key] : null;
    };

    return {
      variant_id: String(read('id') || read('variant_id') || ''),
      quantity: Number(read('quantity')) || 1
    };
  };

  const installCartRequestBridge = () => {
    if (typeof window.fetch !== 'function' || window.fetch.__evanoCroBridge) return;
    const nativeFetch = window.fetch;

    const bridgedFetch = function (...args) {
      const request = args[0];
      const options = args[1] || {};
      const rawUrl = typeof request === 'string' ? request : request?.url || '';
      let isCartAdd = false;

      try {
        isCartAdd = /\/cart\/add(?:\.js)?\/?$/.test(
          new URL(rawUrl, window.location.origin).pathname
        );
      } catch (_) {
        isCartAdd = false;
      }

      if (!isCartAdd) return nativeFetch.apply(this, args);
      const requestDetails = requestCartDetails(options.body);

      return nativeFetch.apply(this, args).then((response) => {
        if (!response.ok) {
          publishCroEvent('add_to_cart_error', {
            source: 'cart_request',
            status: response.status,
            ...requestDetails
          });
          return response;
        }

        response.clone().json().then((item) => {
          publishCroEvent('add_to_cart_success', {
            source: 'cart_request',
            variant_id: String(item?.variant_id || item?.id || requestDetails.variant_id),
            quantity: Number(item?.quantity) || requestDetails.quantity
          });
        }).catch(() => {
          publishCroEvent('add_to_cart_success', {
            source: 'cart_request',
            ...requestDetails
          });
        });

        return response;
      });
    };

    Object.defineProperty(bridgedFetch, '__evanoCroBridge', { value: true });
    window.fetch = bridgedFetch;
  };

  const sampledForInteractionDiagnostics = () => {
    const storageKey = 'evano-inp-diagnostics-v1';
    try {
      const stored = window.sessionStorage.getItem(storageKey);
      if (stored != null) return stored === '1';
      const sampled = Math.random() < 0.25;
      window.sessionStorage.setItem(storageKey, sampled ? '1' : '0');
      return sampled;
    } catch (_) {
      return false;
    }
  };

  const interactionName = (target) => {
    if (!(target instanceof Element)) return 'other';
    if (target.closest('[data-action-quickshop]')) return 'quick_shop_open';
    if (target.closest('[data-swatch-item]')) return 'variant_selection';
    if (target.closest('[data-atc-form], [data-action-atc]')) return 'add_to_cart';
    if (target.closest('.t4s-btn__checkout')) return 'checkout';
    if (target.closest('#t4s-mini_cart')) return 'mini_cart';
    return 'other';
  };

  const installInteractionDiagnostics = () => {
    if (!sampledForInteractionDiagnostics() || !('PerformanceObserver' in window)) {
      return;
    }

    let pendingEntry = null;
    let flushHandle = 0;
    const flush = () => {
      flushHandle = 0;
      if (!pendingEntry) return;
      const entry = pendingEntry;
      pendingEntry = null;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'evano_slow_interaction',
        interaction_name: interactionName(entry.target),
        interaction_duration_ms: Math.round(entry.duration),
        page_path: window.location.pathname
      });
    };
    const scheduleFlush = () => {
      if (flushHandle) return;
      if ('requestIdleCallback' in window) {
        flushHandle = window.requestIdleCallback(flush, { timeout: 1600 });
      } else {
        flushHandle = window.setTimeout(flush, 400);
      }
    };

    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.duration < 200) return;
          if (!pendingEntry || entry.duration > pendingEntry.duration) {
            pendingEntry = entry;
          }
        });
        if (pendingEntry) scheduleFlush();
      });
      observer.observe({ type: 'event', buffered: true, durationThreshold: 120 });
    } catch (_) {
      // Event Timing is not available in every Safari/WebView version.
    }
  };

  installCartRequestBridge();
  installInteractionDiagnostics();
})();
